import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getSnapshot } from 'mobx-state-tree';
import { Box } from '@mui/material';
import { getLoadItemMapSize, pageTypeMapping } from '@mcn-platform/models';
import Renderer, { getConfigFromContext, getData } from './Renderer';
import portalDefaults from './portalDefaults';
import { passesConditions } from './displayConditions';
import Registry from './Registry';
import BlockInput from '../portal/common/BlockInput';
import GlobalConfirmDialog from '../portal/globalDialog/GlobalConfirmDialog';
const getPageKey = (pageType, id) => {
    const prefix = pageTypeMapping[pageType];
    if (!prefix) {
        console.log('Page type not found', pageType);
        return null;
    }
    if (id) {
        return `${prefix}:${id}`;
    }
    return prefix;
};
const getDefaultPageConfig = (pageType, portal) => {
    const prefix = pageTypeMapping[pageType];
    return (portal.pageDefaults && portal.pageDefaults[prefix])
        || portalDefaults.defaults[prefix];
};
const getPageFromRules = (portal, pageType, objectId, requestContext) => {
    const { pageRules } = portal;
    if (!pageRules) {
        console.log('No page rules found for portal', portal);
        return null;
    }
    // look up page rules by type + id (eg category:123)
    const pageKey = getPageKey(pageType, objectId);
    if (pageKey) {
        const rules = pageRules[pageKey];
        // loop through rules to see if we can find one that meets criteria
        const rule = rules?.find((r) => (passesConditions(r.conditions, requestContext)));
        if (rule) {
            console.log('Found rule:', rule);
            return rule.pageId;
        }
    }
    // look up page rules by just type (eg category)
    const pageTypeKey = getPageKey(pageType, null);
    if (pageTypeKey) {
        const rules = pageRules[pageTypeKey];
        // loop through rules to see if we can find one that meets criteria
        const rule = rules?.find((r) => (passesConditions(r.conditions, requestContext)));
        if (rule) {
            console.log('Found rule:', rule);
            return rule.pageId;
        }
    }
    // console.log('no pageKey found for', {
    //   pageType,
    //   objectId,
    // });
    // look up page rules by type (eg home)
    // nothing found - return null
    return null;
};
/**
 * Gets the id of the page config to use for rendering.  Could be a page tied
 * to current object or a default based on pageType.
 *
 * @param portal
 * @param pageType
 * @param objectId
 * @returns
 */
export const getPageConfigId = (portal, pageType, objectId, requestContext) => {
    // get the pageConfig based on rules
    const pageConfigId = getPageFromRules(portal, pageType, objectId, requestContext)
        || getPageKey(pageType, objectId);
    // check if a config with that id exists
    const pageConfig = pageConfigId
        && (portal.configs[pageConfigId]
            || portalDefaults.configs[pageConfigId]);
    // console.log('resulting pageConfig', { pageConfig, pageConfigId });
    return pageConfig ? pageConfigId : getDefaultPageConfig(pageType, portal);
};
export const initPageData = (portal, pageType, objectKey, modifiers, querystring) => {
    const data = {
        rootCategory: portal.categories[0],
        modifiers,
    };
    // load the object into data based on page type
    if (pageType === 'cat') {
        data.categoryId = objectKey;
    }
    if (['hub-learnings', 'hub-documents', 'hub-meetings'].includes(pageType)) {
        data.hubCategoryId = objectKey;
    }
    if (pageType === 'asset') {
        data.assetKey = objectKey;
    }
    if (pageType === 'course' || pageType === 'class') {
        data.courseID = objectKey;
    }
    if (pageType === 'document') {
        data.documentKey = objectKey;
    }
    if (pageType === 'program') {
        data.programID = objectKey;
    }
    if (pageType === 'meeting') {
        data.meetingKey = objectKey;
    }
    if (pageType === 'member') {
        data.memberID = objectKey;
    }
    if (pageType === 'membership') {
        data.membershipID = objectKey;
    }
    if (pageType === 'microcast') {
        data.microcastID = objectKey;
    }
    if (pageType === 'instructor') {
        data.instructorID = objectKey;
    }
    // expect a querystring of items ?type=membership&id=123&type=course&id=456&type=program&id=789
    if (pageType === 'checkout' && querystring?.get) {
        const ids = querystring.get('id');
        const types = querystring.get('type');
        data.checkoutTypes = types;
        data.checkoutIDs = ids;
    }
    return data;
};
export const initPageContext = (portal) => ({
    defaultConfigs: portalDefaults.configs,
    portalConfigs: portal.configs || {},
    portal,
});
const setOverrides = (overrides, currentId, [baseId, overrideId]) => {
    if (overrides[baseId]) {
        // console.log(`Already overridden ${baseId}, not using definition from ${currentId}`);
    }
    else {
        // console.log(`Overridding ${baseId}, with definition from ${currentId}`);
        overrides[baseId] = [overrideId, currentId];
    }
};
/**
 * parents are the immediate parent in the rendering hierarchy.  For components that have
 * children this component itself.  For overrides the parent is the item being overridden.
 *
 * owner is the location where the config is defined.  this is always at template level.
 *
 * @param parentMap Key is the configId of the item.
 * @param ownerMap
 * @param configId
 * @param parentConfig
 * @param overrides
 * @param context
 */
const setParents = (parentMap, ownerMap, configId, parentConfigId, currentTemplateId, overrides, context) => {
    if (!configId)
        return;
    // get all the children
    const config = getConfigFromContext(context, configId);
    if (!config) {
        console.log('No config for configId', configId);
        return;
    }
    const component = Registry[config.component];
    if (!component) {
        console.log(`Component not found: ${config ? config.component : configId}`, config ? config.component : configId);
        return;
    }
    const children = component.meta && component.meta.getChildren
        ? component.meta.getChildren(config)
        : [];
    parentMap[configId] = parentConfigId;
    ownerMap[configId] = currentTemplateId;
    const overrideDef = overrides[configId];
    if (overrideDef) {
        const overrideConfigId = overrideDef[0];
        const owningTemplateId = overrideDef[1];
        // parentMap[overrideConfigId] = configId;
        // ownerMap[overrideConfigId] = owningTemplateId;
        setParents(parentMap, ownerMap, overrideConfigId, configId, owningTemplateId, overrides, context);
    }
    // for each child, set the container (parent)
    children.forEach((childId) => {
        // if the child has an override, get the override
        const override = overrides[childId];
        // for the override or the direct child if there is one get the children and repeat.
        if (override) {
            parentMap[childId] = configId;
            const overrideConfigId = override[0];
            const owningTemplateId = override[1];
            setParents(parentMap, ownerMap, overrideConfigId, childId, owningTemplateId, overrides, context);
        }
        else {
            // direct child
            setParents(parentMap, ownerMap, childId, configId, currentTemplateId, overrides, context);
        }
    });
};
/**
 * Context is an a map of all the pages in the extends tree.
 * This could include portalDefaults and portal templates
 */
export const buildPageDefinition = (configId, context) => {
    let currentId = configId;
    let rootConfigId = '';
    const overrides = {}; // overrides are keyed by the template id
    const templateList = [];
    const ownerMap = {};
    const parentMap = {};
    while (currentId) {
        // get the definition for the extended template
        // console.log('Getting overrides from,', currentId);
        const current = getConfigFromContext(context, currentId);
        if (!current) {
            console.log('Error finding template - not including in page', currentId);
            break;
        }
        rootConfigId = currentId; // this will be root from the last pass through
        // overrides are organized so overrides at the lower (leaf) level
        // take precedence over the same override at the higher level
        if (current.overrides) {
            Object.entries(current.overrides).forEach(
            // eslint-disable-next-line @typescript-eslint/no-loop-func
            (entry) => setOverrides(overrides, currentId, entry));
        }
        templateList.push(currentId);
        // now get the parent config
        // first look at page, then portal, then default @todo
        currentId = current.extends;
    }
    // now start with root and walk the components, with overrides
    setParents(parentMap, ownerMap, rootConfigId, 'root', rootConfigId, overrides, context);
    // current here will be the root template since that is when current.extends is empty.
    return {
        templateList,
        overrides,
        rootConfigId,
        ownerMap,
        parentMap,
    };
};
export const loadPageData = async (pageDefinition, store, data, context, editData = undefined) => {
    if (getLoadItemMapSize() > 0) {
        console.warn('Before ItemMapLoad is not zero', getLoadItemMapSize());
    }
    const dataResult = await getData({
        configId: pageDefinition.rootConfigId,
        data,
        context: { ...context, pageDefinition },
        editData,
    }, store);
    // console.log('------ Get data result', dataResult);
    console.log('store after loadPageData', getSnapshot(store));
    if (getLoadItemMapSize() > 0) {
        console.warn('After ItemMapLoad is not zero', getLoadItemMapSize());
    }
    // hacky way to load course if there is a courseID in data...
    if (data.courseKey) {
        await store.load('course', data.courseKey);
        // data.course = store.getOrLoad('course', data.courseID);
    }
    if (data.programKey) {
        await store.load('program', data.programKey);
        // data.program = store.getOrLoad('program', data.programID);
    }
    // console.log('preload finished');
    return dataResult;
};
const RenderPage = ({ pageDefinition, data, context, editData, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
doPreload = false,
// eslint-disable-next-line arrow-body-style
 }) => {
    // const store = useStore();
    // const [loaded, setLoaded] = useState<boolean>(false);
    // useEffect(() => {
    //   console.log('RenderPage useEffect', pageDefinition);
    //   if (doPreload && !loaded) {
    //     console.log('Starting load');
    //     loadPageData(
    //       pageDefinition,
    //       store,
    //       data,
    //       context,
    //       editData,
    //     ).then(() => {
    //       console.log('Finished load');
    //       setLoaded(true);
    //     });
    //   } else {
    //     console.log('Skipping preload', pageDefinition);
    //   // }
    // });
    // if (doPreload && !loaded) {
    //   return (<LoadingDialog />);
    // }
    //   console.log('Rendering PAGE ---------------------------- ', {
    // //    doPreload,
    // //    loaded,
    //     pageDefinition,
    //   });
    return (_jsxs(Box, { sx: { height: '100%' }, children: [_jsx(Renderer, { configId: pageDefinition.rootConfigId, data: data, context: { ...context, pageDefinition }, editData: editData }), _jsx(GlobalConfirmDialog, {}), _jsx(BlockInput, {})] }));
};
export default RenderPage;
//# sourceMappingURL=RenderPage.js.map